import { archiveMoveTargets } from '../utils/archiveMoveTargets.mjs';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';

// Shared by month and custom-folder views; errors leave the draft intact.
export default function ArchiveFolderChildren({ parent, month, folders, onChange }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [targets, setTargets] = useState([]);
  const request = async (url, method, body) => {
    setBusy(true);
    try {
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body && JSON.stringify(body) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Could not save folder');
      setDraft(null);
      await onChange();
    } catch (error) { showToast(error.message, 'error'); }
    finally { setBusy(false); }
  };
  const beginMove = async (folder) => {
    try {
      const res = await apiFetch(`/api/archive/months/${month.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load destinations');
      const destinations = archiveMoveTargets(data.tree, month.id, folder);
      setTargets(destinations);
      setDraft({ mode: 'move', folder, parent_id: destinations.find(row => row.id.toLowerCase() === folder.parent_id?.toLowerCase())?.id || folder.parent_id });
    } catch (error) { showToast(error.message, 'error'); }
  };
  const save = event => {
    event.preventDefault();
    if (draft.mode === 'create') return request(`/api/archive/${parent.kind === 'month' ? 'months' : 'folders'}/${parent.id}/folders`, 'POST', { name: draft.name, id: draft.id });
    return request(`/api/archive/folders/${draft.folder.id}`, 'PATCH', draft.mode === 'move'
      ? { parent_id: draft.parent_id, expected_revision: draft.folder.revision }
      : { name: draft.name, expected_revision: draft.folder.revision });
  };
  return <section className="mt-6" aria-label="Child folders">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-semibold">Folders</h2>
      {parent.depth < parent.max_depth && <button className="px-4 py-2 border rounded-lg" onClick={() => setDraft({ mode: 'create', name: '', id: crypto.randomUUID() })}>New folder</button>}
    </div>
    {parent.depth >= parent.max_depth && <p className="text-sm text-gray-500">Maximum folder depth reached.</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
      {folders.map(folder => <div key={folder.id} className="border rounded-lg p-4">
        <Link className="font-semibold text-blue-600 break-words" to={`/archive/folder/${folder.id}`}>{folder.name}</Link>
        <p className="text-sm text-gray-500">{folder.photo_count} photos including subfolders · {folder.folder_count} folders</p>
        <div className="flex gap-3 mt-3 text-sm">
          <button onClick={() => setDraft({ mode: 'rename', folder, name: folder.name })}>Rename</button>
          <button onClick={() => beginMove(folder)}>Move</button>
          {folder.photo_count === 0 && folder.folder_count === 0 && <button onClick={() => {
            if (window.confirm(`Delete empty folder “${folder.name}”?`)) request(`/api/archive/folders/${folder.id}`, 'DELETE');
          }}>Delete</button>}
        </div>
      </div>)}
    </div>
    {draft && <div role="dialog" aria-modal="true" aria-label={`${draft.mode} folder`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={save} className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="font-semibold mb-4">{draft.mode === 'move' ? `Move ${draft.folder.name}` : draft.mode === 'rename' ? 'Rename folder' : `New folder in ${parent.name}`}</h3>
        {draft.mode === 'move' ? <label>Destination
          <select className="w-full border rounded p-2" value={draft.parent_id} onChange={e => setDraft({ ...draft, parent_id: e.target.value })}>
            {targets.map(row => <option key={row.id} value={row.id}>{row.label}</option>)}
          </select>
        </label> : <label>Folder name<input autoFocus maxLength={120} required className="w-full border rounded p-2" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>}
        <div className="flex justify-end gap-4 mt-4">
          <button type="button" disabled={busy} onClick={() => setDraft(null)}>Cancel</button>
          <button disabled={busy || (draft.mode !== 'move' && !draft.name.trim())}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>}
  </section>;
}
